/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/no-misused-promises */
import { useState, useCallback, useMemo } from 'react';
import { useApolloClient } from '@apollo/client';
import { Flex } from 'foundations-components/components/Flex';
import { COMMERCE_PAYMENT_TYPE_ID, COMPANY_TYPE_ID, CONTACT_TYPE_ID, INVOICE_TYPE_ID, LINE_ITEM_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { useSendCrmMessageTopic } from 'crm-message-bus/useSendCrmMessageTopic';
import { TOPIC_NAMES } from 'crm-message-bus/types/MessageTopics';
import { InvoiceStatus } from 'invoices-ui-lib/constants/status';
import { Source } from 'invoices-ui-lib/constants/source';
import { getInvoiceProperty } from 'invoices-ui-lib/utils/property';
import { getInvoiceCategories } from 'invoices-ui-lib/utils/invoice';
import useInvoiceCurrencyValidator from 'invoices-ui-lib/hooks/useInvoiceCurrencyValidator';
import { actions } from 'invoices-ui-lib/constants/actions';
import { downloadInvoicePDF } from 'invoices-ui-lib/utils/downloadInvoice';
import InvoiceActionsDropdown from 'invoices-ui-lib/components/InvoiceActionsDropdown';
import { trackInteraction, trackSendInteraction, trackSendUsage, trackUsage } from '../utils/tracking';
import { getIsUngatedForRecordPaymentsAutoCollect } from '../utils/gates';
import { FETCH_INVOICE } from '../graphQL/invoiceHighlightQuery';
import { useFetchInvoiceProperties } from 'invoices-ui-lib/hooks/useFetchInvoiceProperties';
// Modals & Panels
import InvoiceSendEmailModalV2 from 'invoices-ui-lib/components/InvoiceSendEmailModal/InvoiceSendEmailModalV2';
import InvoiceEmailReminderModal from 'invoices-ui-lib/components/Modals/InvoiceEmailReminderModal';
import InvoiceVoidModal from 'invoices-ui-lib/components/Modals/InvoiceVoidModal';
import InvoiceDeleteModal from 'invoices-ui-lib/components/Modals/InvoiceDeleteModal';
import { InvoiceOperation } from 'invoices-ui-lib/types/InvoiceOperation';
import InvoicesEditorIFrameModal from 'invoices-iframe-lib/components/InvoicesEditorIFrameModal';
import RecordPaymentPanel from 'invoices-ui-lib/components/recordPayment/RecordPaymentPanel';
import InvoiceChargePaymentMethodPanel from 'invoices-ui-lib/components/chargePaymentMethod/InvoiceChargePaymentMethodPanel';
// Action Hooks
import useDeleteInvoice from 'invoices-ui-lib/hooks/useDeleteInvoice';
import useEmailReminders from 'invoices-ui-lib/hooks/useEmailReminders';
import useVoidInvoice from 'invoices-ui-lib/hooks/useVoidInvoice';
import useInvoiceEditor from 'invoices-ui-lib/hooks/useInvoiceEditor';
import useRecordPayment from 'invoices-ui-lib/hooks/useRecordPayment';
import useChargePaymentMethod from 'invoices-ui-lib/hooks/useChargePaymentMethod';
import useSendInvoice from 'invoices-ui-lib/hooks/useSendInvoice';
import useDisplayButtonsProps from 'invoices-ui-lib/hooks/useDisplayButtonsProps';
import Raven from 'raven-js';
import usageTracker from '../utils/usageTracker';
import { usePaymentsEligibilityContext } from 'invoices-ui-lib/context/PaymentsEligibilityContext';
// Alerts
import { handleVoidOnError, handleOnGenericError, handleVoidOnSuccess, handleSendOnRateLimitExceededError, handleSendOnError, handleSendOnSuccess, handleDeleteOnSuccess, handleDeleteOnError, handleRecordPaymentOnError, handleRecordPaymentOnErrorOutdated, handleEmailReminderOnSuccess, handleEmailReminderOnError, handleDownloadOnFailure } from 'invoices-ui-lib/utils/actionsDropdownAlerts';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const InvoiceHighlightActions = props => {
  const {
    invoice,
    hasInvoiceEditPermissions,
    hasInvoiceDeletePermissions,
    closeSidebar,
    onObjectCreate,
    paymentStatus,
    updatePreview,
    canEditOpenInvoice,
    canDeleteInvoice,
    canEditReminders,
    gates,
    scopes,
    setPollInterval
  } = props;
  const {
    paymentsEligibility
  } = usePaymentsEligibilityContext();
  const sendObjectUpdatedMessage = useSendCrmMessageTopic(TOPIC_NAMES.UPDATE_OBJECT);
  const sendObjectsUpdatedMessage = useSendCrmMessageTopic(TOPIC_NAMES.UPDATE_OBJECTS);
  const sendObjectCreatedMessage = useSendCrmMessageTopic(TOPIC_NAMES.OBJECT_CREATED);
  const sendAssociationsUpdatedMessage = useSendCrmMessageTopic(TOPIC_NAMES.UPDATE_ASSOCIATIONS);
  const sendObjectDeletedMessage = useSendCrmMessageTopic(TOPIC_NAMES.OBJECT_DELETED);
  const [operation, setOperation] = useState(null);
  const client = useApolloClient();
  const handleUnexpectedValidationError = useCallback(result => Raven.captureMessage('[invoices-highlight-lib] Unexpected invoice validation error', {
    extra: {
      result,
      operation: 'clone'
    }
  }), []);
  const {
    refetch
  } = useFetchInvoiceProperties(invoice.objectId, true);
  const refetchInvoice = useCallback(async () => {
    return await client.refetchQueries({
      include: [FETCH_INVOICE]
    });
  }, [client]);
  const invoiceStatus = getInvoiceProperty(invoice, 'hs_invoice_status');
  const invoiceBalance = getInvoiceProperty(invoice, 'hs_balance_due');
  const {
    loading: currencyValidatorLoading,
    error: currencyValidatorError,
    currencyValidator
  } = useInvoiceCurrencyValidator(![InvoiceStatus.DRAFT, InvoiceStatus.OPEN].includes(invoiceStatus));
  const isUngatedForRecordPaymentsAutoCollect = gates && getIsUngatedForRecordPaymentsAutoCollect(gates);
  const hasAllGates = useCallback((...gatesToCheck) => gatesToCheck.every(gate => gates.includes(gate)), [gates]);
  const hasGate = useCallback(gate => gates.includes(gate), [gates]);
  const maybeCurrencyValidationError = useMemo(() => currencyValidator(getInvoiceProperty(invoice, 'hs_currency')), [currencyValidator, invoice]);
  const trackRecordPaymentUsage = useCallback(({
    action,
    salesTax,
    paymentMethod
  }) => {
    trackUsage(Object.assign({
      action,
      salesTax,
      category: getInvoiceCategories(invoice, paymentsEligibility)
    }, paymentMethod && {
      paymentMethod
    }));
  }, [invoice, paymentsEligibility]);
  const sendInvoiceUpdatedMessage = useCallback(() => sendObjectUpdatedMessage({
    objectTypeId: INVOICE_TYPE_ID,
    objectId: String(invoice.objectId)
  }), [invoice.objectId, sendObjectUpdatedMessage]);
  const sendInvoiceDeletedMessage = useCallback(objectId => sendObjectDeletedMessage({
    objectTypeId: INVOICE_TYPE_ID,
    objectId: String(objectId)
  }), [sendObjectDeletedMessage]);
  const sendInvoiceCreatedMessage = useCallback(objectId => sendObjectCreatedMessage({
    objectTypeId: INVOICE_TYPE_ID,
    objectId: String(objectId)
  }), [sendObjectCreatedMessage]);
  const updateInvoiceAssociations = useCallback(toObjectTypeId => sendAssociationsUpdatedMessage({
    objectTypeId: INVOICE_TYPE_ID,
    toObjectTypeId,
    objectId: String(invoice.objectId)
  }), [invoice.objectId, sendAssociationsUpdatedMessage]);
  const maybeUpdatePaymentIndexTable = useCallback(objectId => {
    if (onObjectCreate) {
      onObjectCreate({
        associationType: COMMERCE_PAYMENT_TYPE_ID,
        objectId: String(objectId)
      });
    }
  }, [onObjectCreate]);
  const updatePaymentsSidebar = useCallback(paymentId => sendObjectsUpdatedMessage([{
    objectTypeId: COMMERCE_PAYMENT_TYPE_ID,
    objectId: String(paymentId)
  }]), [sendObjectsUpdatedMessage]);
  const pollForInvoiceUpdates = useCallback(async () => {
    const MAX_RETRIES = 10;
    let counter = 0;
    let updatedInvoiceBalanceDue = invoiceBalance;
    let refetchedInvoice = null;
    while (counter <= MAX_RETRIES && invoiceBalance === updatedInvoiceBalanceDue) {
      if (counter > 0) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      try {
        refetchedInvoice = await refetch();
        updatedInvoiceBalanceDue = refetchedInvoice.data.crmObject.defaultProperties.hs_balance_due.value;
        counter++;
      } catch (e) {
        counter++;
      }
    }
    return invoiceBalance !== updatedInvoiceBalanceDue ? refetchedInvoice : null;
  }, [refetch, invoiceBalance]);
  const [isRecordPaymentPanelOpen, setIsRecordPaymentPanelOpen] = useState(false);
  const [isSendEmailModalOpen, setSendEmailModalOpen] = useState(false);
  const [isEmailRemindersModalOpen, setEmailRemindersModalOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isVoidModalOpen, setVoidModalOpen] = useState(false);
  const [isInvoiceEditorOpen, setInvoiceEditorOpen] = useState(false);
  const [isChargePaymentMethodPanelOpen, setIsChargePaymentMethodPanelOpen] = useState(false);
  const [preloadedInitialInvoiceData, setPreloadedInitialInvoiceData] = useState(undefined);
  const onChargePaymentPanelCloseSuccess = useCallback(async resetOnFileChargeValidationData => {
    trackUsage({
      action: 'Sucessfully charge an invoice using a stored payment method',
      category: getInvoiceCategories(invoice, paymentsEligibility)
    });
    const updatedInvoice = await pollForInvoiceUpdates();
    if (updatedInvoice) {
      resetOnFileChargeValidationData();
      refetchInvoice().catch(() => handleOnGenericError());
      updateInvoiceAssociations(COMMERCE_PAYMENT_TYPE_ID);
    } else {
      closeSidebar();
    }
  }, [closeSidebar, invoice, paymentsEligibility, pollForInvoiceUpdates, refetchInvoice, updateInvoiceAssociations]);
  const handleGetOnFileChargeValidationFailure = useCallback(error => {
    Raven.captureException(error);
  }, []);
  const {
    handleChargePaymentMethodOnSuccess,
    onFileChargeValidationData,
    onFileChargeValidationLoading,
    resetOnFileChargeValidationData
  } = useChargePaymentMethod(invoice, open => setIsChargePaymentMethodPanelOpen(open), hasInvoiceEditPermissions, onChargePaymentPanelCloseSuccess, handleGetOnFileChargeValidationFailure);
  const {
    emailRemindersLoading,
    emailRemindersFailed,
    onConfirmEmailReminders
  } = useEmailReminders(refetchInvoice, open => setEmailRemindersModalOpen(open), handleEmailReminderOnSuccess, handleEmailReminderOnError, handleOnGenericError);
  const {
    deleteLoading,
    onConfirmDeleteInvoice
  } = useDeleteInvoice(invoice, open => setDeleteModalOpen(open), () => {
    sendInvoiceDeletedMessage(invoice.objectId);
    closeSidebar();
    handleDeleteOnSuccess();
    sendInvoiceUpdatedMessage();
  }, handleDeleteOnError, handleOnGenericError);
  const {
    voidLoading,
    onConfirmVoidInvoice
  } = useVoidInvoice(invoice, open => setVoidModalOpen(open), refetchInvoice, () => {
    handleVoidOnSuccess();
    sendInvoiceUpdatedMessage();
  }, handleVoidOnError, handleOnGenericError);
  const onEditorClose = useCallback(updatedInvoice => {
    if (updatedInvoice) {
      if (operation === InvoiceOperation.CLONE) {
        sendInvoiceCreatedMessage(updatedInvoice.objectId);
      } else {
        sendInvoiceUpdatedMessage();
        updateInvoiceAssociations(COMPANY_TYPE_ID);
        updateInvoiceAssociations(LINE_ITEM_TYPE_ID);
        updateInvoiceAssociations(CONTACT_TYPE_ID);
        refetchInvoice().catch(() => {
          handleOnGenericError();
        });
        if (canEditReminders) {
          setPollInterval(1000);
          setTimeout(() => {
            setPollInterval(0);
          }, 3000);
        }
      }
      resetOnFileChargeValidationData();
    }
  }, [operation, resetOnFileChargeValidationData, sendInvoiceCreatedMessage, sendInvoiceUpdatedMessage, updateInvoiceAssociations, refetchInvoice, canEditReminders, setPollInterval]);
  const {
    handleInvoiceEditorClose
  } = useInvoiceEditor(onEditorClose, open => setInvoiceEditorOpen(open), setPreloadedInitialInvoiceData);
  const openInvoiceEditor = useCallback(invoiceOperation => {
    setOperation(invoiceOperation);
    setInvoiceEditorOpen(true);
  }, [setOperation, setInvoiceEditorOpen]);
  const {
    onFailureRecordPayment,
    handleRecordPaymentOnAdd,
    handleRecordPaymentOnCreate
  } = useRecordPayment(invoice, open => setIsRecordPaymentPanelOpen(open), refetchInvoice, pollForInvoiceUpdates, updatePreview, maybeUpdatePaymentIndexTable, updatePaymentsSidebar, handleRecordPaymentOnError, handleRecordPaymentOnErrorOutdated, handleOnGenericError, trackRecordPaymentUsage);
  const {
    handleSendEmailModalOnClose
  } = useSendInvoice(open => setSendEmailModalOpen(open), handleSendOnSuccess('highlight'), handleSendOnError, handleSendOnRateLimitExceededError);
  const renderModals = () => {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [hasInvoiceEditPermissions && /*#__PURE__*/_jsx(InvoicesEditorIFrameModal, {
        show: isInvoiceEditorOpen,
        objectId: String(invoice.objectId),
        onClose: handleInvoiceEditorClose,
        objectTypeId: INVOICE_TYPE_ID,
        operation: operation,
        preloadedPaymentsEligibility: paymentsEligibility || undefined,
        preloadedInitialInvoiceData: preloadedInitialInvoiceData
      }), isDeleteModalOpen && /*#__PURE__*/_jsx(InvoiceDeleteModal, {
        invoice: invoice,
        loading: deleteLoading,
        onClose: () => setDeleteModalOpen(false),
        onConfirm: onConfirmDeleteInvoice
      }), isRecordPaymentPanelOpen && /*#__PURE__*/_jsx(RecordPaymentPanel, {
        invoice: invoice,
        onReject: () => {
          setIsRecordPaymentPanelOpen(false);
        },
        onFailure: onFailureRecordPayment,
        onAdd: handleRecordPaymentOnAdd,
        onCreate: handleRecordPaymentOnCreate,
        isUngatedForRecordPaymentsAutoCollect: isUngatedForRecordPaymentsAutoCollect
      }), isVoidModalOpen && /*#__PURE__*/_jsx(InvoiceVoidModal, {
        onConfirm: onConfirmVoidInvoice,
        onReject: () => setVoidModalOpen(false),
        loading: voidLoading
      }), isSendEmailModalOpen && /*#__PURE__*/_jsx(InvoiceSendEmailModalV2, {
        invoice: invoice,
        onClose: handleSendEmailModalOnClose,
        onInteraction: trackSendInteraction,
        onUsage: trackSendUsage,
        gates: gates,
        source: Source.INVOICES_HIGHLIGHT_CARD,
        hasScope: scope => scopes.includes(scope),
        hasGate: hasGate,
        tracker: usageTracker,
        trackingLocation: "invoicesHighlightCard"
      }), isChargePaymentMethodPanelOpen && /*#__PURE__*/_jsx(InvoiceChargePaymentMethodPanel, {
        invoice: invoice,
        setIsChargePaymentMethodPanelOpen: setIsChargePaymentMethodPanelOpen,
        resetChargePaymentMethodDisabledReason: resetOnFileChargeValidationData,
        onSuccess: status => void handleChargePaymentMethodOnSuccess(status)
      }), isEmailRemindersModalOpen && /*#__PURE__*/_jsx(InvoiceEmailReminderModal, {
        invoice: invoice,
        onSave: onConfirmEmailReminders,
        onClose: () => setEmailRemindersModalOpen(false),
        loading: emailRemindersLoading,
        failed: emailRemindersFailed
      })]
    });
  };
  const handleActionButtonClick = useCallback(async (key, data) => {
    switch (key) {
      case actions.EDIT:
        trackUsage({
          action: 'Click edit invoice',
          category: getInvoiceCategories(invoice, paymentsEligibility)
        });
        openInvoiceEditor(InvoiceOperation.EDIT);
        setPreloadedInitialInvoiceData(data === null || data === void 0 ? void 0 : data.preloadedInitialInvoiceData);
        break;
      case actions.RECORD_PAYMENT:
        trackUsage({
          action: 'Click record payment',
          category: getInvoiceCategories(invoice, paymentsEligibility)
        });
        setIsRecordPaymentPanelOpen(true);
        break;
      case actions.CLONE:
        trackUsage({
          action: 'Click clone invoice',
          category: getInvoiceCategories(invoice, paymentsEligibility)
        });
        openInvoiceEditor(InvoiceOperation.CLONE);
        setPreloadedInitialInvoiceData(data === null || data === void 0 ? void 0 : data.preloadedInitialInvoiceData);
        break;
      case actions.VIEW:
        trackUsage({
          action: 'Click view invoice',
          category: getInvoiceCategories(invoice, paymentsEligibility)
        });
        break;
      case actions.VIEW_PROPERTY_HISTORY:
        trackUsage({
          action: 'Click view property history',
          category: getInvoiceCategories(invoice, paymentsEligibility)
        });
        break;
      case actions.COPY:
        trackInteraction({
          action: 'Click copy link',
          category: getInvoiceCategories(invoice, paymentsEligibility)
        });
        break;
      case actions.SEND:
        trackUsage({
          action: 'Click send invoice',
          category: getInvoiceCategories(invoice, paymentsEligibility)
        });
        setSendEmailModalOpen(true);
        break;
      case actions.VOID:
        trackUsage({
          action: 'Click void invoice',
          category: getInvoiceCategories(invoice, paymentsEligibility)
        });
        setVoidModalOpen(true);
        break;
      case actions.DELETE:
        trackUsage({
          action: 'Click delete invoice',
          category: getInvoiceCategories(invoice, paymentsEligibility)
        });
        setDeleteModalOpen(true);
        break;
      case actions.CHARGE_PAYMENT_METHOD:
        trackUsage({
          action: 'Click charge payment method',
          category: getInvoiceCategories(invoice, paymentsEligibility)
        });
        setIsChargePaymentMethodPanelOpen(true);
        break;
      case actions.EDIT_REMINDERS:
        trackUsage({
          action: 'Click edit reminders',
          category: getInvoiceCategories(invoice, paymentsEligibility)
        });
        setEmailRemindersModalOpen(true);
        break;
      case actions.DOWNLOAD:
        trackUsage({
          action: 'Click download invoice',
          category: getInvoiceCategories(invoice, paymentsEligibility)
        });
        await downloadInvoicePDF(invoice.objectId, handleDownloadOnFailure);
        break;
      default:
        throw new Error(`handleActionButtonClick encountered unexpected action key. Received: ${key}`);
    }
  }, [invoice, openInvoiceEditor, paymentsEligibility]);
  const {
    isBillableInvoice,
    invoiceHasPayments,
    isAllAssociatedPaymentsFailed,
    hasUpcomingReminderDates,
    isPaymentCollectedAutomatically,
    hasAssociatedSubscription,
    associatedSubscriptionId,
    isBalancePositive,
    hasActiveSubscriptions,
    invoiceLink,
    invoicePropertyHistoryLink,
    hasAssociatedObjectToLink,
    cloneLoading,
    unknownCloneValidationError,
    cloneDisabledReasons,
    shouldDisableRecordPaymentButton
  } = useDisplayButtonsProps(invoice, paymentStatus, gate => gates.includes(gate), handleUnexpectedValidationError);
  const invoiceActionsDropdownProps = {
    permissions: {
      hasInvoiceEditPermissions,
      hasInvoiceDeletePermissions
    },
    invoiceSettings: {
      canEditReminders,
      canEditOpenInvoice,
      canDelete: canDeleteInvoice
    },
    invoice: {
      invoiceId: invoice.objectId,
      isBillableInvoice,
      invoiceStatus: getInvoiceProperty(invoice, 'hs_invoice_status'),
      hasPayments: invoiceHasPayments,
      isAllAssociatedPaymentsFailed,
      hasUpcomingReminderDates,
      isPaymentCollectedAutomatically,
      hasAssociatedSubscription,
      associatedSubscriptionId,
      isBalancePositive,
      hasActiveSubscriptions,
      invoiceLink,
      invoicePropertyHistoryLink,
      hasAssociatedObjectToLink
    },
    currencyValidator: {
      currencyValidatorLoading,
      currencyValidatorError,
      maybeCurrencyValidationError
    },
    actions: {
      clone: {
        cloneLoading,
        unknownCloneValidationError,
        cloneDisabledReasons
      },
      chargePaymentMethod: {
        onFileChargeValidationData,
        onFileChargeValidationLoading
      },
      recordPayment: {
        shouldDisableRecordPaymentButton
      }
    },
    handlers: {
      onClick: handleActionButtonClick,
      updatePreview,
      hasAllGates
    }
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Flex, {
      justifyContent: "end",
      children: /*#__PURE__*/_jsx(Flex, {
        children: /*#__PURE__*/_jsx(InvoiceActionsDropdown, Object.assign({
          "data-test-id": "invoice-highlight-actions-dropdown",
          menuWidth: 210,
          placement: "bottom right",
          buttonUse: "transparent"
        }, invoiceActionsDropdownProps))
      })
    }), renderModals()]
  });
};
export default InvoiceHighlightActions;